<template>
  <div v-if="isVisible" class="modal-overlay">
    <div class="modal-content" @click.stop @keydown="handleKeyDown" tabindex="0" ref="modalContent">
      <div class="modal-header">
        <h3>Aplicar Descuento al Producto</h3>
        <button class="close-btn" @click="closeModal">×</button>
      </div>
      
      <div class="modal-body">
        <div v-if="producto" class="producto-info">
          <p><strong>Producto:</strong> {{ producto.nombre }}</p>
          <p><strong>Precio base (sin ISV):</strong> L. {{ precioBaseSinImpuesto.toFixed(2) }}</p>
          <p><strong>Precio actual (con ISV):</strong> L. {{ producto.precio_final?.toFixed(2) || '0.00' }}</p>
          <p><strong>Cantidad:</strong> {{ producto.cantidad }}</p>
          <p><strong>Tipo ISV:</strong> {{ tipoImpuestoTexto }}</p>
        </div>

        <div class="descuento-section">
          <div class="radio-group">
            <label>
              <input type="radio" v-model="tipoDescuento" value="porcentaje" />
              Descuento por Porcentaje
            </label>
            <label>
              <input type="radio" v-model="tipoDescuento" value="monto" />
              Descuento por Monto (sobre precio sin ISV)
            </label>
          </div>

          <div class="input-group">
            <label v-if="tipoDescuento === 'porcentaje'">Porcentaje (%):</label>
            <label v-else>Monto (L.) - Sobre precio sin ISV:</label>
            <input 
              ref="descuentoInput"
              type="number" 
              v-model="valorDescuento" 
              :min="0" 
              :max="tipoDescuento === 'porcentaje' ? 100 : precioBaseSinImpuesto"
              step="0.01"
              @keydown.enter="aplicarDescuento"
            />
          </div>

          <div v-if="previewDescuento > 0" class="preview">
            <p><strong>Vista previa:</strong></p>
            <p>Descuento sobre precio sin ISV: L. {{ previewDescuento.toFixed(2) }}</p>
            <p>Precio sin ISV después del descuento: L. {{ (precioBaseSinImpuesto - previewDescuento).toFixed(2) }}</p>
            <p>Nuevo total con ISV: L. {{ previewTotal.toFixed(2) }}</p>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="closeModal" class="btn-cancel">Cancelar</button>
        <button @click="aplicarDescuento" class="btn-confirm" :disabled="!valorDescuento || valorDescuento <= 0">
          Aplicar Descuento
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DescuentoProductoModal',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    producto: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      tipoDescuento: 'porcentaje',
      valorDescuento: 0
    }
  },
  computed: {
    // Calcular precio base sin impuesto
    precioBaseSinImpuesto() {
      if (!this.producto) return 0;
      
      // 1. Obtener precio unitario base sin impuesto
      let precioUnitarioBase;
      if (
        this.producto.precio_mayorista > 0 &&
        this.producto.cantidad_activar_mayorista > 0 &&
        this.producto.cantidad >= this.producto.cantidad_activar_mayorista
      ) {
        precioUnitarioBase = this.producto.precio_mayorista;
      } else {
        precioUnitarioBase = this.producto.precio_unitario;
      }
      
      // 2. Multiplicar por cantidad
      let precioBaseSinImpuesto = precioUnitarioBase * this.producto.cantidad;
      
      // 3. Restar descuentos YA aplicados (promociones, tercera edad, etc.)
      if (this.producto.descuento_promociones) {
        precioBaseSinImpuesto -= this.producto.descuento_promociones;
      }
      if (this.producto.descuento_tercera_edad) {
        precioBaseSinImpuesto -= this.producto.descuento_tercera_edad;
      }
      if (this.producto.descuento_general) {
        precioBaseSinImpuesto -= this.producto.descuento_general;
      }
      
      return Math.max(0, precioBaseSinImpuesto);
    },

    tipoImpuestoTexto() {
      if (!this.producto) return '';
      switch(this.producto.impuesto) {
        case 1: return 'ISV 15%';
        case 2: return 'ISV 18%';
        case 3: return 'Exento';
        default: return 'Sin definir';
      }
    },

    previewDescuento() {
      if (!this.valorDescuento || !this.producto) return 0;
      
      if (this.tipoDescuento === 'porcentaje') {
        return (this.precioBaseSinImpuesto * this.valorDescuento) / 100;
      } else {
        return Math.min(this.valorDescuento, this.precioBaseSinImpuesto);
      }
    },

    previewTotal() {
      // Calcular nuevo total con ISV
      const precioConDescuento = this.precioBaseSinImpuesto - this.previewDescuento;
      
      // Aplicar ISV según el campo impuesto de la BD
      let porcentajeImpuesto = 0;
      switch(this.producto?.impuesto) {
        case 1: // ISV 15%
          porcentajeImpuesto = 0.15;
          break;
        case 2: // ISV 18%
          porcentajeImpuesto = 0.18;
          break;
        case 3: // Exento
          porcentajeImpuesto = 0;
          break;
        default:
          porcentajeImpuesto = 0;
      }
      
      return precioConDescuento + (precioConDescuento * porcentajeImpuesto);
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.$refs.modalContent?.focus();
          this.$refs.descuentoInput?.focus();
        });
        this.$emit('modal-focused', true);
      } else {
        this.$emit('modal-focused', false);
      }
    }
  },
  methods: {
    closeModal() {
      this.valorDescuento = 0;
      this.tipoDescuento = 'porcentaje';
      this.$emit('close');
    },
    aplicarDescuento() {
      if (!this.valorDescuento || this.valorDescuento <= 0) return;
      
      this.$emit('aplicar-descuento', {
        tipo: this.tipoDescuento,
        valor: this.valorDescuento,
        descuentoCalculado: this.previewDescuento,
        nuevoTotal: this.previewTotal
      });
      this.closeModal();
    },
    handleKeyDown(event) {
      if (event.key === 'Escape') {
        this.closeModal();
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  outline: none;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 20px;
}

.producto-info {
  background-color: #f5f5f5;
  padding: 15px;
  border-radius: 5px;
  margin-bottom: 20px;
}

.producto-info p {
  margin: 5px 0;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.input-group {
  margin-bottom: 20px;
}

.input-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.input-group input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
}

.preview {
  background-color: #f0fdf4;
  padding: 15px;
  border-radius: 5px;
  border-left: 4px solid #c09d62;
}

.preview p {
  margin: 5px 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #eee;
}

.btn-cancel, .btn-confirm {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-cancel {
  background-color: #6c757d;
  color: #fff;
}

.btn-confirm {
  background-color: #198754;
  color: white;
}

.btn-confirm:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-cancel:hover {
  background-color: #5a6268;
}

.btn-confirm:hover:not(:disabled) {
  background-color: #146c43;
}

/* Modo oscuro */
.dark .modal-content {
  background-color: #1e1e1e;
  color: #e5e7eb;
}

.dark .modal-header {
  border-color: #2b2b2b;
}

.dark .modal-header h3 {
  color: #c09d62;
}

.dark .modal-footer {
  border-color: #2b2b2b;
}

.dark .producto-info {
  background-color: #252525;
  color: #d1d5db;
}

.dark .input-group label {
  color: #9ca3af;
}

.dark .input-group input {
  background-color: #2a2a2a;
  border-color: #444;
  color: #e5e7eb;
}

.dark .preview {
  background-color: #1c1f1a;
  border-left-color: #c09d62;
  color: #d1d5db;
}

.dark .preview strong { color: #c09d62; }

.dark .radio-group label { color: #d1d5db; }

.dark .btn-cancel {
  background-color: #4b5563;
  color: #e5e7eb;
}

.dark .btn-cancel:hover { background-color: #374151; }

.dark .btn-confirm {
  background-color: #198754;
  color: #fff;
}

.dark .btn-confirm:hover:not(:disabled) { background-color: #146c43; }
</style>